import React, { useState, useEffect } from "react"
import _get from "lodash/get"
import _isEmpty from "lodash/isEmpty"
import { useRouter } from "next/router"
import { useSelector, useDispatch } from "react-redux"

import AccountSettingsView from "@/components/AccountSettings/v1/AccountSettingsView"

import { getConfig } from "@/constants/config"
import useAccountSettingsi18n from "@/i18n/useAccountSettingsi18n"
import MyGenericFiller from "@/components/MyGenericFiller"
import {
  getCountryList,
  selectGenericState,
  getBusinessList,
  getGenericListPath,
  editProfile,
  getstateList,
  removeAddress,
  deleteAccount,
  getUserProfile,
  addAddress,
  editAddress,
  setDefaultAddress,
  showToast,
} from "@/store/features/genericSlice"
import {
  selectAuthState,
  logout,
  setUserName,
} from "@/store/features/authSlice"
import { mapStateToCountryHandler, getShortenedUrl,getShortenedUrlwithGeneral } from "@/utils/helper"
import { setMenu } from "@/utils/orders"

const AccountSettings = props => {
  const { data: componentProp = {} } = props
  const { link = "" } = componentProp
  const router = useRouter()
  const [data, setData] = useState({ addresses: [] })
  const [countries, setCountries] = useState([])
  const [country, setCountry] = useState("")
  const [userType, setUserType] = useState("")
  const [stateList, setStateList] = useState([])
  const [businessList, setBusinessList] = useState([])
  const { isAuth, user } = useSelector(selectAuthState)
  const [menuItems, setMenuItems] = useState([])
  const [config, setInitialConfig] = useState({})
  const [loader, setLoader] = useState(true)
  const [homepageUrl, setHomepageUrl] = useState('/')
  const {countryListPath,stateListPath } = useSelector(selectGenericState)
  const dispatch = useDispatch()
  const i18n = useAccountSettingsi18n({
    componentProp,
  })

  const orderAddress = (addresses = [], defaultShippingAddressId = "") => {
    const defaultAddressIndex = addresses?.findIndex(
      address => address?.id === defaultShippingAddressId
    )
    if (defaultAddressIndex !== -1) {
      const defaultAddress = addresses.splice(defaultAddressIndex, 1)[0]
      addresses.unshift(defaultAddress)
    }
    return { addresses }
  }
  const setInitialState = async config => {
    const accountSettingsPath = await getShortenedUrl(
      _get(config, "general.accountSettingsPath", "")
    )
    const myFavoritesPagePath = await getShortenedUrl(
      _get(config, "general.myFavoritesPagePath", "")
    )
    const orderHistoryPath = await getShortenedUrl(
      _get(config, "general.orderHistoryPath", "")
    )
    const myProductsPath = await getShortenedUrl(
      _get(config, "general.myProductsPath", "")
    )
    const myServicesPath = await getShortenedUrl(
      _get(config, "general.myServicesPath", "")
    )
    const orderDetailsUrl = _get(config, "general.orderDetailsPath", "")
    const continueShopUrl = _get(config, "general.continueShopPath", "")
    const currencySign = _get(config, "internationalization.currencySign", "")
    setInitialConfig({
      accountSettingsPath,
      myFavoritesPagePath,
      orderHistoryPath,
      myProductsPath,
      myServicesPath,
      orderDetailsUrl,
      continueShopUrl,
      currencySign,
    })
  }

  const getLocations = () => {
    if (countryListPath && stateListPath) {
      Promise.all([dispatch(getCountryList()), dispatch(getstateList())])?.then(
        (results = []) => {
          const countryData = results[0] ?? {}
          const stateData = results[1] ?? {}
          const mapList = mapStateToCountryHandler(
            countryData?.payload,
            stateData?.payload
          )
          setCountries(mapList)

          setStateList(mapList.find(c => c?.code === country)?.states ?? [])
        }
      )
    }
  }

  const getProfileData = () => {
    dispatch(getUserProfile())
      .unwrap()
      .then((payload = {}) => {
        setData(payload)
        setLoader(false)
      })
  }

  const getBusinessTypes = () => {
    dispatch(getBusinessList())
      .unwrap()
      .then((payload = []) => {
        setBusinessList(payload)
      })
  }

  const handleEditProfile = (prof = {}, cb = () => {}) => {
    dispatch(editProfile(prof))
      .unwrap()
      .then((profile = {}) => {
        const { firstName = "", lastName = "" } = profile
        setData(profile)
        dispatch(setUserName([firstName, lastName]))
        cb()
        dispatch(
          showToast({
            message: i18n?.profileUpdated,
            isVisible: true,
          })
        )
      })
  }

  const handleAddAddress = (addr = []) => {
    dispatch(addAddress(addr))
      .unwrap()
      .then((payload = {}) => {
        const {
          addresses = [],
          defaultShippingAddressId = "",
          error = "",
        } = payload
        const { addresses: orderedAddress } = orderAddress(
          addresses,
          defaultShippingAddressId
        )
        if (!error) {
          setData(data => ({
            ...data,
            addresses: orderedAddress,
            defaultShippingAddressId,
          }))
        }
        addr.forEach((data, i) => {
          if (data?.address?.setPrimary) {
            const addressIndex =
              addr?.length > 0
                ? addresses?.length - (addr?.length - i)
                : addresses?.length - (i + 1)
            const addressDetails = addresses[addressIndex]?.id
            handleEditAddress(addressDetails, {
              setPrimary: data?.address?.setPrimary,
            })
          }
        })
        dispatch(
          showToast({
            message: error ? error : i18n?.addAddressMessage,
            isVisible: true,
          })
        )
      })
  }

  const handleEditAddress = (id = "", addr = {}) => {
    dispatch(editAddress({ id, addr }))
      .unwrap()
      .then((payload = {}) => {
        const { addresses = [], defaultShippingAddressId = "" } = payload
        const { addresses: orderedAddress } = orderAddress(
          addresses,
          defaultShippingAddressId
        )
        setData(data => ({
          ...data,
          addresses: orderedAddress,
          defaultShippingAddressId,
        }))
      })
      .then(() => {
        if (addr?.setPrimary) {
          dispatch(setDefaultAddress(id))
            .unwrap()
            .then((payload = {}) => {
              const { addresses = [], defaultShippingAddressId = "" } = payload
              const { addresses: orderedAddress } = orderAddress(
                addresses,
                defaultShippingAddressId
              )
              setData(data => ({
                ...data,
                addresses: orderedAddress,
                defaultShippingAddressId,
              }))
            })
        }
      })
    dispatch(
      showToast({
        message: i18n?.editAddressMessage,
        isVisible: true,
      })
    )
  }

  const handleDeleteAddress = (id = "") => {
    dispatch(removeAddress(id))
      .unwrap()
      .then((payload = {}) => {
        const { addresses = [], defaultShippingAddressId = "" } = payload
        const { addresses: orderedAddress } = orderAddress(
          addresses,
          defaultShippingAddressId
        )
        setData(data => ({
          ...data,
          addresses: orderedAddress,
          defaultShippingAddressId,
        }))

        dispatch(
          showToast({
            message: i18n?.deleteAddress,
            isVisible: true,
          })
        )
      })
  }
  const handleDeleteAccount = async () => {
    dispatch(deleteAccount())
      .unwrap()
      .then(payload => {
        if (payload) {
          dispatch(
            showToast({
              message: i18n?.accountDeleted,
              isVisible: true,
            })
          )

          dispatch(logout())
          window.location.href = homepageUrl
        }
      })
  }
  const redirect = url => {
    router.push(url)
  }
  const redirectToHome = () => {
    router.push(homepageUrl)
  }
  const signOut = () => {
    dispatch(logout())
  }
  useEffect(() => {
    getConfig().then(async config => {
      setInitialState(config)
      const country = _get(config, "internationalization.country", "")
      setCountry(country)
      const homePageUrl = _get(config, "general.homepageUrl", "/")
      setHomepageUrl(homePageUrl)
    })
    dispatch(getGenericListPath())
    window.scrollTo({
      top: 1,
      behavior: "smooth",
    })
  }, [])

  useEffect(() => {
    try {
      if (isAuth) {
        const { persona = [] } = user ?? {}
        const finalUserType = persona[0]?.split(":")
        getProfileData()
        getLocations()
        getBusinessTypes()
        setUserType(finalUserType)
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error)
    }
  }, [isAuth, countryListPath, stateListPath, country])

  useEffect(() => {
    if (!_isEmpty(config) && isAuth) {
      setMenuItems(setMenu(config, i18n))
    }
  }, [config, isAuth])

  if (loader || _isEmpty(menuItems) || _isEmpty(i18n) || !isAuth) {
    return <MyGenericFiller data={componentProp} title="accountSettings" />
  }
  return (
    <React.Fragment>
      <AccountSettingsView
        i18n={i18n}
        profile={data}
        userType={userType}
        countries={countries}
        country={country}
        stateData={stateList}
        onAddAddress={addr => handleAddAddress(addr)}
        onEditAddress={(id, addr) => handleEditAddress(id, addr)}
        onEditProfile={(prof, cb) => handleEditProfile(prof, cb)}
        onDeleteAddress={id => handleDeleteAddress(id)}
        onDeleteAccount={() => handleDeleteAccount()}
        subscriptionPath={link}
        businessList={businessList}
        data={menuItems}
        redirect={redirect}
        logout={signOut}
        redirectToHome={redirectToHome}
      />
    </React.Fragment>
  )
}

export default AccountSettings
